import { FC, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import $api from "../../shared/api/axios";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Skeleton
} from "@libs/zapi-ui/shadcn";
import { AppButton, AppInput, FigmaTypography } from "@libs/zapi-ui/custom";

const PAGE_SIZE = 25;

type TTemplateProject = {
  id: number;
  name: string;
  updatedAt: string;
  createdByName: string;
};

type TProjectsResponse = {
  items: TTemplateProject[];
  total: number;
  page: number;
  pageSize: number;
};

export type TProjectsTarget = {
  orgId: number;
  slug: string;
  templateName: string;
  orgName: string;
};

type Props = {
  target: TProjectsTarget | null;
  onOpenChange: (open: boolean) => void;
};

export const ProjectListSheet: FC<Props> = ({ target, onOpenChange }) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);
  // Retained so the header doesn't blank out during the close animation (target nulls immediately).
  const [displayTarget, setDisplayTarget] = useState<TProjectsTarget | null>(null);

  useEffect(() => {
    if (target) setDisplayTarget(target);
  }, [target]);

  useEffect(() => {
    setPage(1);
    setSearchInput("");
    setSearch("");
    setConfirmId(null);
  }, [target?.orgId, target?.slug]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isFetching } = useQuery<TProjectsResponse>({
    queryKey: ["admin-template-projects", target?.orgId, target?.slug, page, search],
    queryFn: async () => {
      const response = await $api.get("/admin/templates/projects", {
        params: { organizationId: target?.orgId, slug: target?.slug, page, pageSize: PAGE_SIZE, search: search || undefined }
      });
      return response.data;
    },
    enabled: target !== null,
    placeholderData: keepPreviousData
  });

  const archiveMutation = useMutation({
    mutationFn: (projectId: number) =>
      $api.post(`/admin/templates/projects/${projectId}/archive`, { organizationId: target?.orgId, slug: target?.slug }),
    onSuccess: () => {
      toast.success("Project archived");
      setConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ["admin-template-projects"] });
      queryClient.invalidateQueries({ queryKey: ["admin-templates"] });
      queryClient.invalidateQueries({ queryKey: ["admin-templates-by-org"] });
      queryClient.invalidateQueries({ queryKey: ["admin-templates-by-template"] });
    },
    onError: (error: AxiosError<{ message?: string }>) =>
      toast.error(error?.response?.data?.message ?? "Failed to archive project", { icon: "🔴" })
  });

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const items = data?.items ?? [];

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return (
    <Sheet open={target !== null} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl flex flex-col gap-4 overflow-hidden">
        <SheetHeader>
          <SheetTitle>{displayTarget?.templateName}</SheetTitle>
          <SheetDescription>
            Active projects for <span className="font-medium">{displayTarget?.orgName}</span>
            {!isLoading && <> · {total} {total === 1 ? "project" : "projects"}</>}
          </SheetDescription>
        </SheetHeader>

        <AppInput
          placeholder="Search by name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <div className="flex-1 overflow-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Last written</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-7 w-16 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-[14px] text-gray-500 py-8">
                    No active projects{search ? " match your search" : ""}.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                      <FigmaTypography component="div" variant="body-3" className="text-gray-400">by {p.createdByName}</FigmaTypography>
                    </TableCell>
                    <TableCell className="text-[14px] text-gray-600" title={new Date(p.updatedAt).toLocaleString()}>
                      {formatDistanceToNow(new Date(p.updatedAt), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      {confirmId === p.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <AppButton
                            size="sm"
                            appearance="contained"
                            color="red400"
                            className="text-[12px] leading-[18px]"
                            disabled={archiveMutation.isPending}
                            onClick={() => archiveMutation.mutate(p.id)}
                          >
                            Confirm
                          </AppButton>
                          <AppButton
                            size="sm"
                            appearance="text"
                            className="text-[12px] leading-[18px] text-gray-500"
                            disabled={archiveMutation.isPending}
                            onClick={() => setConfirmId(null)}
                          >
                            Cancel
                          </AppButton>
                        </div>
                      ) : (
                        <AppButton
                          size="sm"
                          appearance="text"
                          color="red400"
                          className="text-[12px] leading-[18px]"
                          disabled={archiveMutation.isPending}
                          onClick={() => setConfirmId(p.id)}
                        >
                          Archive
                        </AppButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between text-[14px] text-gray-500">
          <span>
            {total > 0 && `Page ${page} of ${pageCount}`}
            {isFetching && !isLoading && " · updating…"}
          </span>
          <div className="flex items-center gap-2">
            <AppButton
              size="sm"
              appearance="outlined"
              className="text-[12px] leading-[18px]"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </AppButton>
            <AppButton
              size="sm"
              appearance="outlined"
              className="text-[12px] leading-[18px]"
              disabled={page >= pageCount || isFetching}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              Next
            </AppButton>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
